<script setup lang="ts">
import type { SearchHistoryItem } from '~/composables/search/useSearchHistory'
import { faClockRotateLeft, faSparkles, faXmark } from '@fortawesome/pro-light-svg-icons'

const props = defineProps<{
  searchHistory: SearchHistoryItem[]
  context: HistoryContext
}>()

const emit = defineEmits(['select', 'focusInput'])

const { removeSearchFromHistory } = useSearchHistory(props.context)
const listRef = useTemplateRef('list')

const { onKeydown, focusFirst } = useListKeyboardNavigation(listRef, () => emit('focusInput'))

defineExpose({ focusFirst })

function formatMetadata(item: SearchHistoryItem): string {
  if (!item.topResult) {
    return ''
  }

  if (item.topResult.nodeType === 'author') {
    return 'Forfatter'
  }

  const parts: string[] = []
  if (item.topResult.publishedAt) {
    // Full dates alongside edition numbers can take up a lot of space,
    // so when an edition number is present we only show the year for now (TODO: clarify this with design).
    if (item.topResult.editionNumber) {
      parts.push(`${item.topResult.editionNumber}. utgave ${formatDate(new Date(item.topResult.publishedAt), { year: 'numeric' })}`)
    }
    else {
      parts.push(formatDate(new Date(item.topResult.publishedAt), { year: 'numeric', month: 'long', day: 'numeric' }))
    }
  }

  if (item.topResult.author) {
    parts.push(item.topResult.author)
  }

  return parts.join(' - ')
}
</script>

<template>
  <div class="flex items-center justify-between gap-xs">
    <span class="text-caption text-primary">Sist søkt</span>
    <Button
      :to="{ name: 'ki-sok-index' }"
      :icon="faSparkles"
      icon-position="right"
      class="!h-[42px] !px-s !py-[10px] !gap-2xs !rounded-lg !bg-blurple-100"
    >
      KI-søk
    </Button>
  </div>
  <ul ref="list" aria-label="Søkeresultater">
    <li v-for="(item, index) in searchHistory" :key="index" class="flex items-center w-full text-small" :class="{ 'border-b border-b-border-secondary': index !== searchHistory.length - 1 }">
      <NuxtLink
        :to="item.topResult?.url || { name: 'search-index', query: { sq: item.term, page: undefined } }"
        class="no-underline py-xs pl-2xs pr-m flex items-center w-full gap-xs hover:bg-surface-hover focus-visible:bg-surface-hover rounded-md" @keydown="onKeydown" @click="$emit('select')" @mousedown.prevent
      >
        <Icon :icon="faClockRotateLeft" size="sm" class="text-primary" />
        <div class="flex flex-col gap-3xs grow min-w-0">
          <span class="text-primary line-clamp-2 text-small">{{ item.topResult?.title || item.term }}</span>
          <span v-if="formatMetadata(item)" class="text-caption text-secondary truncate">
            {{ formatMetadata(item) }}
          </span>
        </div>
        <Button variant="tertiary" class="!p-0 !bg-transparent" :aria-label="`Fjern ${item} fra søkehistorikk`" @mousedown.prevent @click.prevent="removeSearchFromHistory(item.term)">
          <Icon :icon="faXmark" size="sm" class="!text-primary" />
        </Button>
      </NuxtLink>
    </li>
  </ul>
</template>
